import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { API_ENDPOINTS } from './constants';
import { Header } from './components/ui';
import { tokens } from './styles/tokens';

function MyBetsPage() {
  const navigate = useNavigate();
  const { 
    isAuthenticated, 
    loading, 
    logout, 
    getAccessToken,
    userInfo
  } = useAuth();
  const [bets, setBets] = useState([]);
  const [loadingBets, setLoadingBets] = useState(false);
  const [betsError, setBetsError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const fetchUserBets = async () => {
    try {
      setLoadingBets(true);
      setBetsError('');
      const accessToken = getAccessToken();
      
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await fetch(`${API_ENDPOINTS.USER_API}/bet`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBets(data.bets || []);
      } else if (response.status === 404) {
        setBets([]);
      } else {
        throw new Error(`Failed to load bets: ${response.status}`);
      }
      
    } catch (error) {
      console.error('Error fetching user bets:', error);
      setBetsError(error.message);
    } finally {
      setLoadingBets(false);
    }
  };

  // Fetch bets when component mounts for authenticated users
  useEffect(() => {
    if (isAuthenticated) {
      fetchUserBets();
    }
  }, [isAuthenticated]);

  // Filter and sort bets
  const filteredAndSortedBets = bets
    .filter(bet => {
      if (filterStatus === 'all') return true;
      return (bet.status || 'pending').toLowerCase() === filterStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.gameDate || b.gameTime || 0) - new Date(a.gameDate || a.gameTime || 0);
        case 'oldest':
          return new Date(a.gameDate || a.gameTime || 0) - new Date(b.gameDate || b.gameTime || 0);
        case 'amount_high':
          return parseFloat(b.amount || 0) - parseFloat(a.amount || 0);
        case 'amount_low':
          return parseFloat(a.amount || 0) - parseFloat(b.amount || 0);
        default:
          return 0;
      }
    });

  const getStatusColors = (status) => {
    const normalizedStatus = (status || 'pending').toLowerCase();
    switch(normalizedStatus) {
      case 'won':
        return {
          bg: `${tokens.colors.accent[900]}33`,
          border: tokens.colors.accent[700],
          text: tokens.colors.accent[400],
          dot: '#22c55e'
        };
      case 'lost':
        return {
          bg: `${tokens.colors.error[900]}33`,
          border: tokens.colors.error[700],
          text: tokens.colors.error[400],
          dot: '#ef4444'
        };
      case 'refunded':
        return {
          bg: '#7c3aed33',
          border: '#7c3aed',
          text: '#a855f7',
          dot: '#7c3aed'
        };
      default: // pending
        return {
          bg: '#fbbf2433',
          border: '#fbbf24',
          text: '#fbbf24',
          dot: '#fbbf24'
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatGameTime = (dateString) => {
    if (!dateString) return 'Time unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid time';
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: tokens.colors.gradients.dark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: `3px solid ${tokens.colors.neutral[800]}`,
            borderTopColor: tokens.colors.accent[400],
            borderRadius: '50%',
            margin: '0 auto',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: tokens.colors.neutral[400], marginTop: tokens.spacing[4] }}>
            Loading your bets...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const menuItems = [
    { label: 'Home', href: '/home' },
    { label: 'Sports', href: '/sports' },
    { label: 'My Bets', href: '/my-bets', active: true }
  ];

  const stats = {
    total: bets.length,
    won: bets.filter(b => b.status === 'won').length,
    lost: bets.filter(b => b.status === 'lost').length,
    pending: bets.filter(b => !b.status || b.status === 'pending').length,
    refunded: bets.filter(b => b.status === 'refunded').length,
    totalVolume: bets.reduce((sum, bet) => sum + parseFloat(bet.amount || 0), 0),
    totalWinnings: bets.filter(b => b.status === 'won').reduce((sum, bet) => {
      const payout = parseFloat(bet.totalPayout || bet.payout || 0);
      return sum + payout;
    }, 0)
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: tokens.colors.gradients.dark,
      position: 'relative'
    }}>
      <Header
        logo="ELXR Sportsbook"
        user={userInfo ? {
          name: userInfo.given_name || userInfo.name,
          email: userInfo.email
        } : null}
        onLogout={handleLogout}
        menuItems={menuItems}
      />

      {/* Animated Background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '400px',
          height: '400px',
          background: `radial-gradient(circle, ${tokens.colors.accent[900]}15 0%, transparent 70%)`,
          filter: 'blur(60px)',
          animation: 'float 20s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: '500px',
          height: '500px',
          background: `radial-gradient(circle, ${tokens.colors.primary[900]}15 0%, transparent 70%)`,
          filter: 'blur(60px)',
          animation: 'float 25s ease-in-out infinite reverse'
        }} />
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          33% { transform: translate(30px, -30px) rotate(120deg); }
          66% { transform: translate(-20px, 20px) rotate(240deg); }
        }
      `}</style>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: `${tokens.spacing[8]} ${tokens.spacing[6]}`,
        position: 'relative',
        zIndex: 1
      }}>
        {/* Page Header */}
        <div style={{
          marginBottom: tokens.spacing[8]
        }}>
          <h1 style={{
            fontSize: tokens.typography.fontSize['4xl'],
            fontWeight: tokens.typography.fontWeight.extrabold,
            background: tokens.colors.gradients.glow,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: tokens.spacing[4],
            textAlign: 'center'
          }}>
            My Betting History
          </h1>
          <p style={{
            color: tokens.colors.neutral[400],
            fontSize: tokens.typography.fontSize.lg,
            textAlign: 'center',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Track all your bets, analyze your performance, and stay on top of your game
          </p>
        </div>

        {/* Stats Overview */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: tokens.spacing[4],
          marginBottom: tokens.spacing[8]
        }}>
          {[
            { label: 'Total Bets', value: stats.total, icon: '🎲', color: tokens.colors.neutral[400] },
            { label: 'Won', value: stats.won, icon: '🏆', color: tokens.colors.accent[400] },
            { label: 'Lost', value: stats.lost, icon: '❌', color: tokens.colors.error[400] },
            { label: 'Pending', value: stats.pending, icon: '⏳', color: '#fbbf24' },
            { label: 'Volume', value: `${stats.totalVolume.toFixed(2)} XRP`, icon: '💰', color: tokens.colors.primary[400] },
            { label: 'Winnings', value: `${stats.totalWinnings.toFixed(2)} XRP`, icon: '💎', color: tokens.colors.accent[400] }
          ].map((stat, index) => (
            <div key={index} style={{
              background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${tokens.colors.neutral[800]}`,
              borderRadius: tokens.borderRadius.xl,
              padding: tokens.spacing[6],
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '-10px',
                right: '-10px',
                fontSize: '60px',
                opacity: 0.1
              }}>
                {stat.icon}
              </div>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <p style={{
                  color: tokens.colors.neutral[500],
                  fontSize: tokens.typography.fontSize.sm,
                  marginBottom: tokens.spacing[2]
                }}>
                  {stat.label}
                </p>
                <p style={{
                  color: stat.color,
                  fontSize: tokens.typography.fontSize['2xl'],
                  fontWeight: tokens.typography.fontWeight.bold
                }}>
                  {stat.value}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Filters and Controls */}
        <div style={{
          background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${tokens.colors.neutral[800]}`,
          borderRadius: tokens.borderRadius.xl,
          padding: tokens.spacing[6],
          marginBottom: tokens.spacing[6],
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: tokens.spacing[4],
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[4], flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2] }}>
              <label style={{
                color: tokens.colors.neutral[400],
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.medium
              }}>
                Filter:
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  background: tokens.colors.dark[700],
                  border: `1px solid ${tokens.colors.neutral[800]}`,
                  borderRadius: tokens.borderRadius.lg,
                  color: tokens.colors.neutral[0],
                  padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
                  fontSize: tokens.typography.fontSize.sm
                }}
              >
                <option value="all">All Bets</option>
                <option value="pending">Pending</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2] }}>
              <label style={{
                color: tokens.colors.neutral[400],
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.medium
              }}>
                Sort:
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  background: tokens.colors.dark[700],
                  border: `1px solid ${tokens.colors.neutral[800]}`,
                  borderRadius: tokens.borderRadius.lg,
                  color: tokens.colors.neutral[0],
                  padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
                  fontSize: tokens.typography.fontSize.sm
                }}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="amount_high">Highest Amount</option>
                <option value="amount_low">Lowest Amount</option>
              </select>
            </div>
          </div>

          <button
            onClick={fetchUserBets}
            disabled={loadingBets}
            style={{
              background: tokens.colors.gradients.accent,
              border: 'none',
              color: tokens.colors.dark[900],
              padding: `${tokens.spacing[3]} ${tokens.spacing[6]}`,
              borderRadius: tokens.borderRadius.lg,
              fontWeight: tokens.typography.fontWeight.semibold,
              cursor: loadingBets ? 'not-allowed' : 'pointer',
              opacity: loadingBets ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacing[2]
            }}
          >
            <span style={{ fontSize: '1rem' }}>🔄</span>
            {loadingBets ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Bets List */}
        {loadingBets ? (
          <div style={{
            background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${tokens.colors.neutral[800]}`,
            borderRadius: tokens.borderRadius.xl,
            padding: tokens.spacing[12],
            textAlign: 'center'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: `3px solid ${tokens.colors.neutral[800]}`,
              borderTopColor: tokens.colors.accent[400],
              borderRadius: '50%',
              margin: '0 auto',
              animation: 'spin 1s linear infinite'
            }} />
            <p style={{
              color: tokens.colors.neutral[400],
              marginTop: tokens.spacing[4]
            }}>
              Loading your bets...
            </p>
          </div>
        ) : betsError ? (
          <div style={{
            background: `${tokens.colors.error[900]}33`,
            border: `1px solid ${tokens.colors.error[700]}`,
            borderRadius: tokens.borderRadius.xl,
            padding: tokens.spacing[8],
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '3rem',
              marginBottom: tokens.spacing[4]
            }}>
              ⚠️
            </div>
            <p style={{
              color: tokens.colors.error[400],
              fontSize: tokens.typography.fontSize.lg,
              marginBottom: tokens.spacing[4]
            }}>
              Failed to load your bets
            </p>
            <p style={{
              color: tokens.colors.neutral[500],
              fontSize: tokens.typography.fontSize.sm
            }}>
              {betsError}
            </p>
          </div>
        ) : filteredAndSortedBets.length === 0 ? (
          <div style={{
            background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${tokens.colors.neutral[800]}`,
            borderRadius: tokens.borderRadius.xl,
            padding: tokens.spacing[12],
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: tokens.spacing[6],
              opacity: 0.3
            }}>
              🎲
            </div>
            <h3 style={{
              color: tokens.colors.neutral[300],
              fontSize: tokens.typography.fontSize.xl,
              fontWeight: tokens.typography.fontWeight.semibold,
              marginBottom: tokens.spacing[4]
            }}>
              {filterStatus === 'all' ? 'No bets placed yet' : `No ${filterStatus} bets found`}
            </h3>
            <p style={{
              color: tokens.colors.neutral[500],
              fontSize: tokens.typography.fontSize.base,
              marginBottom: tokens.spacing[8],
              maxWidth: '400px',
              margin: `0 auto ${tokens.spacing[8]}`
            }}>
              {filterStatus === 'all' 
                ? 'Start your betting journey by placing your first bet!'
                : `Try adjusting your filters to see more bets.`
              }
            </p>
            {filterStatus === 'all' && (
              <button
                onClick={() => navigate('/home')}
                style={{
                  background: tokens.colors.gradients.accent,
                  border: 'none',
                  color: tokens.colors.dark[900],
                  padding: `${tokens.spacing[4]} ${tokens.spacing[8]}`,
                  borderRadius: tokens.borderRadius.lg,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  fontSize: tokens.typography.fontSize.base,
                  cursor: 'pointer'
                }}
              >
                Start Betting
              </button>
            )}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gap: tokens.spacing[6]
          }}>
            {filteredAndSortedBets.map((bet, index) => {
              const statusColors = getStatusColors(bet.status);
              return (
                <div 
                  key={index} 
                  onClick={() => navigate(`/current_bet/${bet.id || bet._id || index}`, { state: { bet } })}
                  style={{
                    background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${tokens.colors.neutral[800]}`,
                    borderRadius: tokens.borderRadius.xl,
                    padding: tokens.spacing[8],
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = statusColors.border;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 10px 30px ${tokens.colors.dark[900]}66`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = tokens.colors.neutral[800];
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}>
                  {/* Status indicator stripe */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: statusColors.dot
                  }} />

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: tokens.spacing[20], // Increased gap significantly for larger horizontal space
                    alignItems: 'start'
                  }}>
                    {/* Main bet content */}
                    <div>
                      {/* Header with status and date */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: tokens.spacing[4],
                        marginBottom: tokens.spacing[4],
                        flexWrap: 'wrap'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: tokens.spacing[2],
                          padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
                          background: statusColors.bg,
                          border: `1px solid ${statusColors.border}`,
                          borderRadius: tokens.borderRadius.full
                        }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: statusColors.dot
                          }} />
                          <span style={{
                            color: statusColors.text,
                            fontSize: tokens.typography.fontSize.sm,
                            fontWeight: tokens.typography.fontWeight.semibold,
                            textTransform: 'uppercase'
                          }}>
                            {bet.status || 'Pending'}
                          </span>
                        </div>
                        <span style={{
                          color: tokens.colors.neutral[500],
                          fontSize: tokens.typography.fontSize.sm
                        }}>
                          Placed {formatDate(bet.created_at || bet.placedAt)}
                        </span>
                      </div>

                      {/* Game information */}
                      <div style={{ marginBottom: tokens.spacing[4] }}>
                        <h3 style={{
                          color: tokens.colors.neutral[0],
                          fontSize: tokens.typography.fontSize.xl,
                          fontWeight: tokens.typography.fontWeight.bold,
                          marginBottom: tokens.spacing[2]
                        }}>
                          {bet.homeTeam && bet.awayTeam ? 
                            `${bet.homeTeam} vs ${bet.awayTeam}` : 
                            `Game ${bet.gameId || bet.game_id || 'Unknown'}`
                          }
                        </h3>
                        {bet.sport && (
                          <p style={{
                            color: tokens.colors.neutral[400],
                            fontSize: tokens.typography.fontSize.sm,
                            marginBottom: tokens.spacing[1]
                          }}>
                            Sport: {bet.sport}
                          </p>
                        )}
                        {(bet.gameDate || bet.gameTime) && (
                          <p style={{
                            color: tokens.colors.neutral[400],
                            fontSize: tokens.typography.fontSize.sm
                          }}>
                            Game Time: {formatGameTime(bet.gameTime || bet.gameDate)}
                          </p>
                        )}
                      </div>

                      {/* Bet details */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: tokens.spacing[4],
                        padding: `${tokens.spacing[4] * 0.8}px`, // 20% shorter
                        background: `${tokens.colors.dark[700]}44`,
                        borderRadius: tokens.borderRadius.lg,
                        border: `1px solid ${tokens.colors.neutral[800]}`
                      }}>
                        <div>
                          <p style={{
                            color: tokens.colors.neutral[500],
                            fontSize: tokens.typography.fontSize.xs,
                            textTransform: 'uppercase',
                            fontWeight: tokens.typography.fontWeight.semibold,
                            marginBottom: `${tokens.spacing[1] * 0.8}px`, // 20% shorter
                            paddingLeft: tokens.spacing[2]
                          }}>
                            Prediction
                          </p>
                          <p style={{
                            color: tokens.colors.neutral[0],
                            fontSize: tokens.typography.fontSize.base,
                            fontWeight: tokens.typography.fontWeight.medium,
                            paddingLeft: tokens.spacing[2]
                          }}>
                            {bet.outcome || bet.prediction || 'Not specified'}
                          </p>
                        </div>
                        {bet.odds && (
                          <div>
                            <p style={{
                              color: tokens.colors.neutral[500],
                              fontSize: tokens.typography.fontSize.xs,
                              textTransform: 'uppercase',
                              fontWeight: tokens.typography.fontWeight.semibold,
                              marginBottom: tokens.spacing[1]
                            }}>
                              Odds
                            </p>
                            <p style={{
                              color: tokens.colors.accent[400],
                              fontSize: tokens.typography.fontSize.base,
                              fontWeight: tokens.typography.fontWeight.semibold
                            }}>
                              {bet.odds} {bet.americanOdds && `(${bet.americanOdds})`}
                            </p>
                          </div>
                        )}
                        {bet.betType && (
                          <div>
                            <p style={{
                              color: tokens.colors.neutral[500],
                              fontSize: tokens.typography.fontSize.xs,
                              textTransform: 'uppercase',
                              fontWeight: tokens.typography.fontWeight.semibold,
                              marginBottom: tokens.spacing[1]
                            }}>
                              Bet Type
                            </p>
                            <p style={{
                              color: tokens.colors.neutral[0],
                              fontSize: tokens.typography.fontSize.base,
                              textTransform: 'capitalize'
                            }}>
                              {bet.betType.replace('_', ' ')}
                            </p>
                          </div>
                        )}
                        {bet.bookmaker && (
                          <div>
                            <p style={{
                              color: tokens.colors.neutral[500],
                              fontSize: tokens.typography.fontSize.xs,
                              textTransform: 'uppercase',
                              fontWeight: tokens.typography.fontWeight.semibold,
                              marginBottom: tokens.spacing[1]
                            }}>
                              Bookmaker
                            </p>
                            <p style={{
                              color: tokens.colors.neutral[0],
                              fontSize: tokens.typography.fontSize.base
                            }}>
                              {bet.bookmaker}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Transaction details */}
                      {bet.xrpTransactionHash && (
                        <div style={{
                          marginTop: tokens.spacing[4],
                          padding: `${tokens.spacing[3] * 0.8}px`, // 20% shorter
                          background: `${tokens.colors.primary[900]}22`,
                          borderRadius: tokens.borderRadius.lg,
                          border: `1px solid ${tokens.colors.primary[800]}`
                        }}>
                          <p style={{
                            color: tokens.colors.neutral[500],
                            fontSize: tokens.typography.fontSize.xs,
                            textTransform: 'uppercase',
                            fontWeight: tokens.typography.fontWeight.semibold,
                            marginBottom: `${tokens.spacing[1] * 0.8}px`, // 20% shorter
                            paddingLeft: tokens.spacing[2]
                          }}>
                            XRP Transaction
                          </p>
                          <p style={{
                            color: tokens.colors.primary[400],
                            fontSize: tokens.typography.fontSize.sm,
                            fontFamily: tokens.typography.fontFamily.mono,
                            paddingLeft: tokens.spacing[2],
                            wordBreak: 'break-all'
                          }}>
                            {bet.xrpTransactionHash}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Amount and payout section */}
                    <div style={{
                      textAlign: 'right',
                      minWidth: '150px'
                    }}>
                      <div style={{
                        padding: tokens.spacing[4],
                        background: `${tokens.colors.neutral[800]}33`,
                        borderRadius: tokens.borderRadius.lg,
                        border: `1px solid ${tokens.colors.neutral[700]}`,
                        marginBottom: tokens.spacing[3],
                        textAlign: 'center'
                      }}>
                        <p style={{
                          color: tokens.colors.neutral[500],
                          fontSize: tokens.typography.fontSize.xs,
                          textTransform: 'uppercase',
                          fontWeight: tokens.typography.fontWeight.semibold,
                          marginBottom: tokens.spacing[1]
                        }}>
                          Bet Amount
                        </p>
                        <p style={{
                          color: tokens.colors.neutral[300],
                          fontSize: tokens.typography.fontSize['2xl'],
                          fontWeight: tokens.typography.fontWeight.bold
                        }}>
                          {bet.amount || '0'} XRP
                        </p>
                      </div>

                      {/* Status-based Payout Box */}
                      {bet.amount && (
                        <div style={{
                          padding: tokens.spacing[4],
                          background: statusColors.bg,
                          borderRadius: tokens.borderRadius.lg,
                          border: `1px solid ${statusColors.border}`,
                          marginBottom: tokens.spacing[3],
                          textAlign: 'center'
                        }}>
                          <p style={{
                            color: tokens.colors.neutral[500],
                            fontSize: tokens.typography.fontSize.xs,
                            textTransform: 'uppercase',
                            fontWeight: tokens.typography.fontWeight.semibold,
                            marginBottom: tokens.spacing[1]
                          }}>
                            {bet.status === 'won' ? 'Paid Out' : 
                             bet.status === 'refunded' ? 'Refunded' : 
                             'Potential Payout'}
                          </p>
                          <p style={{
                            color: statusColors.text,
                            fontSize: tokens.typography.fontSize['2xl'],
                            fontWeight: tokens.typography.fontWeight.bold
                          }}>
                            {bet.status === 'won' ? 
                              `${parseFloat(bet.totalPayout || bet.payout || (parseFloat(bet.amount) * parseFloat(bet.odds || 1))).toFixed(2)} XRP` :
                             bet.status === 'refunded' ? 
                              `${parseFloat(bet.amount).toFixed(2)} XRP` :
                             bet.odds ? 
                              `${(parseFloat(bet.amount) * parseFloat(bet.odds)).toFixed(2)} XRP` :
                              `${parseFloat(bet.amount).toFixed(2)} XRP`}
                          </p>
                        </div>
                      )}

                      {bet.payout && (
                        <div style={{
                          padding: tokens.spacing[4],
                          background: `${statusColors.bg}`,
                          borderRadius: tokens.borderRadius.lg,
                          border: `1px solid ${statusColors.border}`
                        }}>
                          <p style={{
                            color: tokens.colors.neutral[500],
                            fontSize: tokens.typography.fontSize.xs,
                            textTransform: 'uppercase',
                            fontWeight: tokens.typography.fontWeight.semibold,
                            marginBottom: tokens.spacing[1]
                          }}>
                            {bet.status === 'won' ? 'Payout' : 'Potential Payout'}
                          </p>
                          <p style={{
                            color: statusColors.text,
                            fontSize: tokens.typography.fontSize.xl,
                            fontWeight: tokens.typography.fontWeight.bold
                          }}>
                            {bet.payout} XRP
                          </p>
                        </div>
                      )}

                      {bet.status === 'won' && bet.payout && (
                        <div style={{
                          marginTop: tokens.spacing[3],
                          padding: tokens.spacing[2],
                          background: `${tokens.colors.accent[900]}33`,
                          borderRadius: tokens.borderRadius.md
                        }}>
                          <p style={{
                            color: tokens.colors.accent[400],
                            fontSize: tokens.typography.fontSize.sm,
                            fontWeight: tokens.typography.fontWeight.semibold
                          }}>
                            +{(parseFloat(bet.payout) - parseFloat(bet.amount || 0)).toFixed(2)} XRP profit
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyBetsPage; 